import styles from './index.module.less';
import { ImageWithCardArt } from '../image-with-card-art';
import type { ButtonProps, DisclaimerLink } from '../../../core/types';
import { FontType, Color, Align, Size } from '../../../core/enums';
import { BackgroundImage } from '../../atoms/background-image';
import { CardArt } from '../../atoms/card-art';
import { Row, Column } from '../../atoms/grid';
import { GroupedButtons } from '../../buttons-and-links/grouped-button';
import { useBreakpoint } from '../../styleless/breakpoint';
import { Typography } from '../../archived/typography';
import Element from '../../../core/Element';
interface BannerCardArtWithCtaAndBackgroundProps {
  data: {
    card: {
      cardImage: string;
      cardUrl?: string;
      cardName: string;
      cardFee?: string;
    };
    ctas: ButtonProps[];
    disclaimerLinks: DisclaimerLink[];
    bgImage: string;
    title?: string;
    copy?: string;
  };
  $imagePosition?: 'left' | 'right' | 'bottom' | 'top';
  qeId?: string;
  customization?: {
    gradient?: {
      angle: string;
      colors: string[];
    };
  };
}

export const BannerCardArtWithCtaAndBackground = ({
  data: {
    card: { cardImage, cardName, cardFee },
    bgImage,
    title,
    copy,
    ctas,
    disclaimerLinks,
  },
  qeId,
}: BannerCardArtWithCtaAndBackgroundProps): React.ReactElement => {
  const { sm } = useBreakpoint();
  return (
    <Element className={styles.root} qeId={qeId}>
      {sm ? (
        <Element>
          <Element className={styles.mobileBgImg}>
            <ImageWithCardArt
              data={{
                backgroundImage: bgImage,
                cardImage,
                cardImageAlt: cardName,
              }}
              $position="bottom"
            />
          </Element>
          <Element theme={{ paddingLeft: 20, paddingRight: 20 }}>
            <Typography
              Component="h3"
              $family={FontType.BentonMedium}
              $size={20}
              $lineHeight={28}
              theme={{
                marginTop: 10,
                marginBottom: 0,
                color: Color.DeepBlue,
                textAlign: Align.Center,
              }}
            >
              {cardName}
            </Typography>
            <Typography
              $family={FontType.HelveticaRegular}
              $size={14}
              $lineHeight={16}
              theme={{
                marginTop: 10,
                marginBottom: 0,
                color: Color.DeepBlue,
                textAlign: Align.Center,
              }}
            >
              {cardFee}
            </Typography>
            <Typography
              Component="h3"
              $family={FontType.BentonRegular}
              $size={24}
              $lineHeight={32}
              theme={{
                marginTop: 10,
                marginBottom: 0,
                color: Color.DeepBlue,
              }}
            >
              {title}
            </Typography>
            <Typography
              $family={FontType.HelveticaRegular}
              $size={16}
              $lineHeight={24}
              theme={{
                marginTop: 10,
                marginBottom: 0,
                color: Color.DeepBlue,
              }}
            >
              {copy}
            </Typography>
          </Element>
          <Element theme={{ textAlign: Align.Center }}>
            <GroupedButtons
              data={{ ctas, disclaimerLinks }}
              customization={{
                ctaContainerTheme: {
                  marginTop: 30,
                },
              }}
            />
          </Element>
        </Element>
      ) : (
        <Element className={styles.backgroundImg}>
          <BackgroundImage imageUrl={bgImage} $withGradient="light">
            <Row
              theme={{
                paddingTop: 35,
                paddingBottom: 35,
                paddingLeft: 30,
                paddingRight: 30,
              }}
            >
              <Column $md="4" $lg="3">
                <Element className={styles.cardArt}>
                  <CardArt
                    src={cardImage}
                    alt={cardName}
                    $size={Size.SM}
                    theme={{
                      paddingTop: 20,
                    }}
                  />
                  <Typography
                    $size={20}
                    $lineHeight={28}
                    $family={FontType.BentonMedium}
                    Component="h3"
                    theme={{
                      marginTop: 10,
                      marginBottom: 0,
                      color: Color.DeepBlue,
                    }}
                  >
                    {cardName}
                  </Typography>
                  <Typography
                    $size={14}
                    $lineHeight={16}
                    $family={FontType.HelveticaRegular}
                    theme={{
                      marginTop: 10,
                      marginBottom: 0,
                      color: Color.DeepBlue,
                    }}
                  >
                    {cardFee}
                  </Typography>
                </Element>
              </Column>
              <Column $md="8" $lg="6">
                <Element theme={{ paddingLeft: 15 }}>
                  <Typography
                    Component="h3"
                    $family={FontType.BentonRegular}
                    $size={24}
                    $lineHeight={32}
                    theme={{
                      marginTop: 10,
                      marginBottom: 0,
                      color: Color.DeepBlue,
                    }}
                  >
                    {title}
                  </Typography>
                  <Typography
                    $family={FontType.HelveticaRegular}
                    $size={16}
                    $lineHeight={24}
                    theme={{
                      marginTop: 10,
                      marginBottom: 0,
                      color: Color.DeepBlue,
                    }}
                  >
                    {copy}
                  </Typography>
                  <Element className={styles.ctaLinks}>
                    <GroupedButtons
                      data={{ ctas, disclaimerLinks }}
                      customization={{
                        ctaContainerTheme: {
                          marginTop: 30,
                        },
                      }}
                    />
                  </Element>
                </Element>
              </Column>
            </Row>
          </BackgroundImage>
        </Element>
      )}
    </Element>
  );
};
